import { useState, useEffect } from 'react';
import {
  Flex,
  Input,
  Heading,
  Stack,
  useColorModeValue,
  Box,
  Button,
  Icon
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import ConfettiExplosion from 'react-confetti-explosion';
import { isValidEmail } from 'utils/string'; // Make sure this path is correct
import { FiArrowRight } from 'react-icons/fi';

export interface WaitlistCardProps {
  showButton?: boolean;
  buttonLoading?: boolean;
  onButtonClick?: (email: string) => void;
  title?: string
}

export default function WaitlistCard({
  showButton = true,
  buttonLoading = false,
  onButtonClick,
  title = 'Join for early access'
}: WaitlistCardProps) {
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [previousLoadingState, setPreviousLoadingState] = useState(false);
  const textColor = useColorModeValue("white", "black");
  const bgColor = useColorModeValue("gray.800", "white");
  const borderProfileColor = useColorModeValue("white", "transparent");

  // Detect when loading completes and submission was successful
  useEffect(() => {
    if (previousLoadingState && !buttonLoading && submissionSuccess) {
      setShowConfetti(true);
      setEmail('');
      setIsEmailValid(false);
      // Hide confetti after 3 seconds
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
    setPreviousLoadingState(buttonLoading);
  }, [buttonLoading, submissionSuccess, previousLoadingState]);

  const onEmailInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const inputValue: string = (event.target as HTMLInputElement).value;
    setEmail(inputValue);
    setIsEmailValid(isValidEmail(inputValue));
    setSubmissionSuccess(false);
  };

  const handleButtonClick = () => {
    if (onButtonClick && isEmailValid && email.length > 0) {
      onButtonClick(email);
      setSubmissionSuccess(true);
    }
  }

  function renderButton() {
    return (
      <Button
        colorScheme={'orange'}
        rightIcon={<Icon as={FiArrowRight} />}
        isLoading={buttonLoading}
        onClick={handleButtonClick}
        disabled={!isEmailValid || email.length === 0 || buttonLoading}
        paddingX={8}
      >
        Join Waitlist
      </Button>
    );
  }


  function renderHeader() {
    return (
      <Heading fontSize="xl">{title}</Heading>
    );
  }

  function renderInput() {
    return (
      <Input
        backgroundColor={useColorModeValue('white', 'gray.700')}
        color={useColorModeValue('black', 'white')}
        placeholder={'Your Email*'}
        onChange={onEmailInputChange}
        isInvalid={!isEmailValid && email.length > 0}
        disabled={buttonLoading}
        value={email}
      />
    );
  }

  return (
    <Box position="relative" width="100%">
      {showConfetti && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={1000}
        >
          <ConfettiExplosion
            force={0.6}
            duration={3000}
            particleCount={100}
            width={1200}
          />
        </Box>
      )}
      <motion.div
        animate={submissionSuccess && !buttonLoading ? {
          scale: [1, 1.02, 1],
          transition: { duration: 0.5 }
        } : {}}
      >
        <Flex
          direction={"column"}
          mb='24px'
          maxH='330px'
          gap={8}
          align='start'
          backdropFilter='blur(21px)'
          boxShadow='0px 2px 5.5px rgba(0, 0, 0, 0.02)'
          border='2px solid'
          borderColor={borderProfileColor}
          color={textColor}
          bg={bgColor}
          p='24px'
          width="100%"
          borderRadius='20px'>
          {renderHeader()}
          <Stack width="100%" textAlign={'left'}>
            <Flex direction={{ 'base': 'column', 'md': 'row' }} justifyContent={'center'} gap={4}>
              {renderInput()}
              {renderButton()}
            </Flex>
          </Stack>
        </Flex>
      </motion.div>
    </Box>
  );
}
